import { useSelector } from 'react-redux';
import getStaticPropsQuestionPropsSelector from '/business/shared/state/selectors/staticProps/getStaticPropsQuestionPropsSelector';
import getUnitSelector from '/business/shared/state/selectors/units/getUnitSelector';
import type { unitDataEmissionType } from '/business/shared/components/widgets/application-meta/SysPrefs/SysPrefTypes';
import type { emissionDropdown } from '/index';
import getVehicleSizesSelector from '/business/shared/state/selectors/application/step1/getVehicleSizesSelector';
import type { RootState } from '/business/shared/state/store';

export function useGetEmissionsFromQuestion({
  question,
  hideIfMissingVehicleSize = false,
  questionStaticPropsOverrideApiKey = undefined,
}: {
  question?: {
    apiKey?: string;
  };
  hideIfMissingVehicleSize?: boolean;
  questionStaticPropsOverrideApiKey?: string;
}) {
  const questionStaticProps = useSelector(getStaticPropsQuestionPropsSelector);
  const fullEmissionList = useSelector(getUnitSelector);

  const questionStaticPropsKey = questionStaticPropsOverrideApiKey
    ? questionStaticPropsOverrideApiKey
    : question?.apiKey;
  const vehicleSizesFactory = useSelector((state: RootState) => getVehicleSizesSelector(state, { question }));
  const questionProps = questionStaticProps.find((prop) => prop.key === questionStaticPropsKey);
  const emissionGroup = questionProps?.emissionGroup || null;

  let filteredEmissions: unitDataEmissionType[] = [];
  if (vehicleSizesFactory) {
    filteredEmissions =
      fullEmissionList?.collection?.filter((emission) => {
        if (!emissionGroup) {
          return false;
        }
        if (emissionGroup === 'vehicle-specific-callback' && hideIfMissingVehicleSize) {
          const fuelLocal = emission.systemId;
          const localFactor = vehicleSizesFactory(fuelLocal);
          if (localFactor.length === 0) {
            return false;
          }
        }
        return emission.energyGroups.includes(emissionGroup);
      }) ?? [];
  }

  const mappedForDropdown: emissionDropdown[] = filteredEmissions.map((emission) => ({
    name: emission.systemId,
    displayName: emission.label,
    units: emission.units?.map((unit) => ({
      name: unit.unitId,
      displayName: unit.label || '',
    })),
  }));

  return {
    key: question?.apiKey,
    requiresInitiativeEmissionBreakdown: filteredEmissions.length > 1,
    mappedForDropdown,
    filteredEmissions,
  };
}
